import { ConsentManager, mountConsentBanner } from '@ingresse/awa-analytics-web';
import { Locale, enUS, es as esES, pt as ptPT, ptBR } from 'date-fns/locale';
import { ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import i18n, { initI18n } from '../../i18n';
import { normalizeLocale, resolveStoredLocale, writeLocaleCookie } from '../../i18n/locale';

type CurrentLocaleProps = {
  locale: string;
}

type InterpolationParams = Record<string, string | number>;

export type i18nContextProps = {
  updateLocale: (newLocale: string) => void;
  translate: (keys: string[], params?: InterpolationParams) => string;
  supportedLocales: ({ lang }: {lang: string}) => Locale;
  currentLocale: CurrentLocaleProps;
};

const I18nContext = createContext({} as i18nContextProps);

initI18n();

/**
 * The date-fns locale for the language on screen.
 *
 * `pt` in date-fns IS European Portuguese — the Brazilian one is the separate
 * `pt-BR`. Mapping pt-PT to `pt` is the correct pairing, not a fallback.
 *
 * Normalizes its input so a legacy `pt_br` still finds its calendar.
 */
const DATE_FNS_LOCALES: Record<string, Locale> = {
  'pt-BR' : ptBR,
  'pt-PT' : ptPT,
  'es-ES' : esES,
  'en-US' : enUS,
};

function I18nProvider({ children }: { children: ReactNode }) {
  const [currentLocale, setCurrentLocale] = useState<CurrentLocaleProps>(
    () => ({ locale : i18n.language }),
  );

  const { t } = useTranslation();
  const [queryParam] = useSearchParams();

  const supportedLocales = ({ lang }: {lang: string}): Locale =>
    DATE_FNS_LOCALES[normalizeLocale(lang) ?? ''] ?? ptBR;

  /**
   * Translates a dot-path of keys, warning when the key does not exist.
   */
  const translate = (keys: string[], params?: InterpolationParams) => {
    const path = keys.join('.');
    const translated = t(path, params);

    if (translated === path) {
      console.warn(`Translation not found for path: ${path}`);
      return 'Translation not found';
    }

    return translated;
  };

  /**
   * Switches the application's language and shares the choice.
   *
   * Takes ANY spelling — the canonical tag, the legacy `pt_br` this app used to
   * persist, a bare `pt`, sloppy casing — and normalizes before doing anything.
   * The implementation this replaces split on `_` and upper-cased the second
   * half, which threw `Cannot read properties of undefined` for any value
   * without a region, and handed i18next whatever it was given.
   *
   * An unsupported value is IGNORED rather than applied: leaving the visitor on
   * the language they already have beats resetting them to the default.
   */
  const updateLocale = (newLocale: string): void => {
    const locale = normalizeLocale(newLocale);
    if (!locale) return;

    i18n.changeLanguage(locale);
    writeLocaleCookie(locale);
    setCurrentLocale({ locale });
  };

  /**
   * Persists whatever was resolved at boot, so the language survives the jump
   * to the next Ingresse application even when it came from the query string
   * or the device rather than from the cookie.
   *
   * The stored choice wins; the parameter only speaks when nothing is stored.
   */
  useEffect(() => {
    // Runs once, on mount: this settles the language for the session. Adding
    // `queryParam` or `updateLocale` to the deps would re-run it on every
    // navigation and undo a language the visitor picked mid-checkout.
    updateLocale(
      resolveStoredLocale() ?? normalizeLocale(queryParam.get('locale')) ?? i18n.language,
    );
  }, []);

  /**
   * Keeps the SDK consent banner in the language the checkout is showing.
   * `bootstrap` (main.tsx) mounts it once, so without this it would stay frozen
   * at the language it mounted with. Re-mounting re-renders in place — the SDK
   * reuses the same DOM node — and only matters while consent is still pending,
   * which is the only state where the banner is on screen.
   */
  useEffect(() => {
    const onLanguageChanged = (nextLocale: string): void => {
      if (ConsentManager.needsRenewal()) {
        mountConsentBanner({ locale : nextLocale });
      }
    };

    i18n.on('languageChanged', onLanguageChanged);
    return () => {
      i18n.off('languageChanged', onLanguageChanged);
    };
  }, []);

  const shared = useMemo(() => ({
    updateLocale, translate, supportedLocales, currentLocale
  }), [
    updateLocale, translate, supportedLocales, currentLocale
  ]);

  return (
    <I18nContext.Provider value={shared}>
      {children}
    </I18nContext.Provider>
  );
}

I18nProvider.displayName = 'I18nProvider';
export const useI18nContext = (): i18nContextProps => useContext(I18nContext);

export { I18nContext, I18nProvider };
